import { memo, type CSSProperties, type ReactNode, type Ref } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CloseIcon, LightbulbIcon } from '@utils/icons';
import type { ITourBulbStep, TourSide } from './types';
import styles from './PageTour.module.css';

/** What the bulb SAYS. None of it changes while the spotlight glides, so it is
 *  memoized away from the shell's per-frame placement. */
export interface ITourBulbContentProps {
  tourId: string;
  step: ITourBulbStep;
  /** 1-based position among the anchored steps, and their count. 0 on a modal step. */
  number: number;
  total: number;
  /** Placement mode, read here for one reason only: the "do this" hint names a
   *  real control, so it only makes sense while that control is on screen.
   *  It flips when a hole appears or disappears, never during a glide. */
  mode: 'modal' | 'anchored';
  /** What the bulb offers: `modal` is the script's own primary/secondary
   *  buttons (welcome, finish), `steps` is dots + Back + Next. Driven by the
   *  step's SHAPE, not by the placement — a targeted step whose target is
   *  missing renders centred but still walks the tour, and has no
   *  `primary`/`secondary` copy of its own. */
  footer: 'modal' | 'steps';
  isFirstAnchored: boolean;
  isLast: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

/** Where the bulb SITS. Re-rendered on every frame of a glide. */
export interface ITourBulbProps extends ITourBulbContentProps {
  side: TourSide;
  tail: number;
  left: number;
  top: number;
  ref?: Ref<HTMLDivElement>;
}

/** The bulb's copy. All of it comes from `tours.<tourId>.<stepId>.*`. */
const TourBulbContent = memo(function TourBulbContent({
  tourId, step, number, total, mode, footer, isFirstAnchored, isLast, onNext, onBack, onSkip,
}: ITourBulbContentProps) {
  const { t, i18n } = useTranslation();
  const key = (suffix: string) => `tours.${tourId}.${step.id}.${suffix}`;
  const optional = (suffix: string): string | null => (i18n.exists(key(suffix)) ? t(key(suffix)) : null);
  const rich = (k: string): ReactNode => <Trans i18nKey={k} components={{ b: <b />, i: <i /> }} />;
  const Figure = step.figure;
  const bodyKey = step.copy?.body ?? 'body';
  const body2Key = step.copy?.body2 ?? 'body2';
  const body2 = optional(body2Key);
  // The "do this" line names the real control, so it only makes sense while
  // that control is on screen — the same condition that hides Next below.
  const hint = step.action && mode === 'anchored' ? t(key('hint')) : null;
  const caption = Figure ? optional('caption') : null;

  const foot = footer === 'modal' ? (
    <div className={styles.foot}>
      {optional('secondary') ? <button type="button" className={styles.btn} data-quiet onClick={onSkip}>{t(key('secondary'))}</button> : null}
      <span className={styles.spacer} />
      <button type="button" className={styles.btn} data-primary onClick={onNext}>{t(key('primary'))}</button>
    </div>
  ) : (
    <div className={styles.foot}>
      <div className={styles.dots} aria-hidden="true">
        {Array.from({ length: total }, (_, i) => <i key={i} data-on={i + 1 === number ? '' : undefined} />)}
      </div>
      {isFirstAnchored ? null : <button type="button" className={styles.btn} onClick={onBack}>{t('tours.common.back')}</button>}
      {/* An action step hands the click to the real control — unless that
          control was never found, in which case Next is the only way on. */}
      {step.action && mode === 'anchored' ? null : (
        <button type="button" className={styles.btn} data-primary onClick={onNext}>
          {optional('next') ?? t(isLast ? 'tours.common.finish' : 'tours.common.next')}
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className={styles.head}>
        <span className={styles.icon}><LightbulbIcon size={22} strokeWidth={1.8} /></span>
        <span className={styles.step}>{footer === 'modal' ? t('tours.common.label') : t('tours.common.step', { n: number, total })}</span>
        <button type="button" className={styles.close} aria-label={t('tours.common.skip')} onClick={onSkip}>
          <CloseIcon size={16} />
        </button>
      </div>
      <h3 className={styles.title}>{t(key('title'))}</h3>
      <p className={styles.body}>{rich(key(bodyKey))}</p>
      {body2 ? <p className={styles.body}>{rich(key(body2Key))}</p> : null}
      {Figure ? (
        <figure className={styles.figure}>
          <Figure />
          {caption ? <figcaption className={styles.caption}>{caption}</figcaption> : null}
        </figure>
      ) : null}
      {hint ? <div className={styles.hint}><span className={styles.hintDot} />{hint}</div> : null}
      {foot}
    </>
  );
});

/**
 * The bulb: the placed shell around the copy. A glide re-renders only this —
 * `left`/`top`/`tail`/`side` change every frame while the content's props do
 * not, so the memoized content is skipped.
 */
export function TourBulb({ side, tail, left, top, ref, ...content }: ITourBulbProps) {
  const { t } = useTranslation();
  // `--tail` is a custom property, which `CSSProperties` cannot express.
  const style: CSSProperties & Record<'--tail', string> = { left, top, '--tail': `${tail}px` };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={t('tours.common.label')}
      // Load-bearing, not decoration: Base UI's `FloatingFocusManager` marks
      // everything outside an open modal dialog `aria-hidden` and spares only
      // `[aria-live]` elements. The bulb is portaled to `document.body`, so
      // without this the steps inside a modal dialog vanish from the
      // accessibility tree the moment it opens. Do not remove or move it.
      aria-live="polite"
      tabIndex={-1}
      className={styles.bulb}
      data-mode={content.mode}
      data-footer={content.footer}
      data-side={content.mode === 'anchored' ? side : undefined}
      style={style}
    >
      <TourBulbContent {...content} />
    </div>
  );
}
